import {
  AlignmentType,
  BorderStyle,
  Document,
  Footer,
  HeightRule,
  ImageRun,
  PageOrientation,
  Packer,
  Paragraph,
  ShadingType,
  Table,
  TableCell,
  TableRow,
  TextRun,
  UnderlineType,
  WidthType,
  VerticalAlign,
  Header,
} from 'docx';
import harediLogo from '../../../../../assets/logos/haredi-logo.png';
import { Gender } from '../../../../../api/types/user/user-summary.type';
import { StudyCertificatePDFData } from './study-certificate-html-template';

export type { StudyCertificatePDFData } from './study-certificate-html-template';

// A4 in DXA (twentieths of a point, 1 point = 1/72 inch).
const A4_WIDTH_DXA = 11906;
const A4_HEIGHT_DXA = 16838;

// Word maps Hebrew characters to the "complex script" font slot, so we have to
// set ascii / hAnsi / cs all to David — otherwise the Hebrew runs render in
// whatever default cs font Word picks (usually Arial / Times New Roman).
const HEBREW_FONT = { ascii: 'David', hAnsi: 'David', cs: 'David' };

const COLOR_GOLD = 'C9A84C';
const COLOR_GREEN = '1F5C1F';
const COLOR_FOOTER_GRAY = '7F8C8D';

// Cell / table border set used to mark every internal divider/header table as
// "no visible borders" — we only use tables for layout, not for grids.
const NO_BORDERS = {
  top: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
  bottom: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
  left: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
  right: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
  insideHorizontal: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
  insideVertical: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
};

function toGregorianDate(date: Date): string {
  return date.toLocaleDateString('he-IL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function studentVerb(gender?: Gender): string {
  if (gender === 'female') return 'הינה סטודנטית';
  if (gender === 'male') return 'הינו סטודנט';
  return 'הינו/ה סטודנט/ית';
}

async function loadLogoBytes(): Promise<Uint8Array> {
  const res = await fetch(harediLogo);
  const buf = await res.arrayBuffer();
  return new Uint8Array(buf);
}

function triggerBlobDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function heText(
  text: string,
  options: {
    bold?: boolean;
    size?: number;
    color?: string;
    underline?: boolean;
    rightToLeft?: boolean;
  } = {},
): TextRun {
  const {
    bold = false,
    size = 26,
    color,
    underline = false,
    rightToLeft = true,
  } = options;
  return new TextRun({
    text,
    bold,
    size,
    color,
    underline: underline ? { type: UnderlineType.SINGLE } : undefined,
    rightToLeft,
    font: HEBREW_FONT,
  });
}

function buildHeaderTable(logoBytes: Uint8Array, gregDate: string): Table {
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    visuallyRightToLeft: true,
    borders: NO_BORDERS,
    rows: [
      new TableRow({
        children: [
          new TableCell({
            width: { size: 50, type: WidthType.PERCENTAGE },
            borders: NO_BORDERS,
            verticalAlign: VerticalAlign.CENTER,
            children: [
              new Paragraph({
                bidirectional: true,
                alignment: AlignmentType.LEFT,
                spacing: { before: 0, after: 60 },
                children: [heText('המרכז החרדי להכשרה מקצועית', { size: 20 })],
              }),
              new Paragraph({
                bidirectional: true,
                alignment: AlignmentType.LEFT,
                spacing: { before: 0, after: 60 },
                children: [
                  heText('www.charedicts.org.il', {
                    bold: true,
                    color: COLOR_GREEN,
                    size: 20,
                    rightToLeft: false,
                  }),
                ],
              }),
              new Paragraph({
                bidirectional: true,
                alignment: AlignmentType.LEFT,
                spacing: { before: 0, after: 0 },
                children: [heText(gregDate, { size: 20, rightToLeft: false })],
              }),
            ],
          }),
          new TableCell({
            width: { size: 50, type: WidthType.PERCENTAGE },
            borders: NO_BORDERS,
            children: [
              new Paragraph({
                alignment: AlignmentType.LEFT,
                spacing: { before: 0, after: 0 },
                children: [
                  new ImageRun({
                    data: logoBytes,
                    // 469x524 native; preserve aspect at ~110pt height.
                    transformation: { width: 99, height: 110 },
                  }),
                ],
              }),
            ],
          }),
        ],
      }),
    ],
  });
}

function buildDividerTable(fillColor: string, heightDxa: number): Table {
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: NO_BORDERS,
    rows: [
      new TableRow({
        height: { value: heightDxa, rule: HeightRule.EXACT },
        children: [
          new TableCell({
            width: { size: 100, type: WidthType.PERCENTAGE },
            borders: NO_BORDERS,
            shading: {
              type: ShadingType.CLEAR,
              color: 'auto',
              fill: fillColor,
            },
            margins: { top: 0, bottom: 0, left: 0, right: 0 },
            children: [
              new Paragraph({
                spacing: { before: 0, after: 0 },
                children: [],
              }),
            ],
          }),
        ],
      }),
    ],
  });
}

export async function generateStudyCertificateWord(
  data: StudyCertificatePDFData,
): Promise<void> {
  const {
    studentName,
    studentId,
    studentGender,
    displayProgramName,
    displayAcademicYear,
  } = data;

  const logoBytes = await loadLogoBytes();
  const gregDate = toGregorianDate(new Date());
  const verb = studentVerb(studentGender);

  const doc = new Document({
    styles: {
      default: {
        document: {
          run: {
            font: HEBREW_FONT,
            size: 26,
          },
        },
      },
    },
    sections: [
      {
        headers: {
          default: new Header({
            children: [buildHeaderTable(logoBytes, gregDate)],
          }),
        },
        properties: {
          page: {
            size: {
              width: A4_WIDTH_DXA,
              height: A4_HEIGHT_DXA,
              orientation: PageOrientation.PORTRAIT,
            },
            margin: {
              top: 720,
              right: 720,
              bottom: 720,
              left: 720,
            },
          },
        },
        footers: {
          default: new Footer({
            children: [
              new Paragraph({
                alignment: AlignmentType.RIGHT,
                children: [
                  heText('מסמך זה נוצר באמצעות מערכת דירו www.diro-tech.com', {
                    size: 16,
                    color: COLOR_FOOTER_GRAY,
                  }),
                ],
              }),
            ],
          }),
        },
        children: [
          /*buildHeaderTable(logoBytes, gregDate),*/
          /*new Paragraph({
            spacing: { before: 0, after: 0 },
            children: [],
          }),
          buildDividerTable(COLOR_GOLD, 60),
          new Paragraph({
            spacing: { before: 0, after: 40 },
            children: [],
          }),
          buildDividerTable(COLOR_GREEN, 100),
          new Paragraph({
            spacing: { before: 600, after: 0 },
            children: [],
          }),*/
          new Paragraph({
            spacing: { before: 600, after: 0 },
            children: [],
          }),
          new Paragraph({
            bidirectional: true,
            alignment: AlignmentType.LEFT,
            spacing: { before: 0, after: 360 },
            children: [heText('לכל המעוניין,', { size: 26 })],
          }),
          new Paragraph({
            bidirectional: true,
            alignment: AlignmentType.LEFT,
            spacing: { before: 0, after: 480 },
            children: [
              heText('הנדון: ', { bold: true, size: 30 }),
              heText('אישור לימודים', {
                bold: true,
                size: 30,
                underline: true,
              }),
            ],
          }),
          new Paragraph({
            bidirectional: true,
            alignment: AlignmentType.LEFT,
            spacing: { before: 0, after: 240, line: 480 },
            children: [
              heText('הנני לאשר בזה כי ', { size: 28 }),
              heText(studentName, { bold: true, size: 28 }),
              heText(', ת.ז. ', { size: 28 }),
              heText(studentId, {
                bold: true,
                size: 28,
                rightToLeft: false,
              }),
              heText(`, ${verb} במגמת `, { size: 28 }),
              heText(displayProgramName, { bold: true, size: 28 }),
              heText(' ב״המרכז החרדי להכשרה מקצועית״ בשנת הלימודים ', {
                size: 28,
              }),
              heText(displayAcademicYear, { bold: true, size: 28 }),
              heText('.', { size: 28 }),
            ],
          }),
          new Paragraph({
            bidirectional: true,
            alignment: AlignmentType.LEFT,
            spacing: { before: 800, after: 0 },
            children: [heText('בכבוד רב,', { size: 26 })],
          }),
          new Paragraph({
            bidirectional: true,
            alignment: AlignmentType.LEFT,
            spacing: { before: 0, after: 0 },
            children: [heText('מזכירות', { size: 26 })],
          }),
        ],
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  triggerBlobDownload(blob, `אישור_לימודים_${data.studentName}.docx`);
}
